import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Modal, Row, Col, Alert, Spinner, Input, Button } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../helpers/api';
import FeatherIcon from 'feather-icons-react';
import * as XLSX from 'xlsx';
import FileModal from '../components/Common/importExportModal';
import PermissionGuard from '../components/Common/PermissionGuard';

const Location = () => {
    document.title = "Locations | ArcMedix";
    const [organizations, setOrganizations] = useState(null);
    const [organizationId, setOrganizationId] = useState('');
    const [locations, setLocations] = useState([]);
    const [modal_large, setmodal_large] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [deleteModal, setDeleteModal] = useState(false);
    const [count, setCount] = useState(10);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [fileModal, setFileModal] = useState(false);

    // New state for Sublocation Modal
    const [sublocationModal, setSublocationModal] = useState({
        isOpen: false,
        parentLocation: null,
        sublocations: [],
        loading: false,
        mode: 'view', // 'view', 'add', 'edit'
        selectedSublocation: null
    });
    
    // State for sublocation delete modal
    const [sublocationDeleteModal, setSublocationDeleteModal] = useState({
        isOpen: false,
        sublocation: null
    });

    const fetchOrganizations = async() => {
        const {data} = await api.get('organization/all');
        setOrganizations(data.response);
        if (data.response && data.response.length > 0) {
            setOrganizationId(data.response[0].id);
        }
    };

    const fetchLocations = async () => {
        if (!organizationId) return;
        
        setLoading(true);
        try {
            const {data} = await api.get(
                `organization-location/get-by-organization-id?organizationId=${organizationId}&count=${count}${
                    searchTerm ? `&search=${searchTerm}` : ''
                }`
            );
            
            setLocations(data.response[0]?.locations || []);
            setHasMore(data.response[0]?.locations.length >= count);
        } catch (error) {
            console.error('Error fetching locations:', error);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    // New function to handle sublocations modal
    const handleSublocationsClick = (location) => {
        setSublocationModal({
            isOpen: true,
            parentLocation: location,
            sublocations: location.subLocations || [],
            loading: false,
            mode: 'view',
            selectedSublocation: null
        });
    };

    // Function to add new sublocation
    const handleAddSublocation = () => {
        setSublocationModal(prev => ({
            ...prev,
            mode: 'add',
            selectedSublocation: {
                locationName: '',
                locationAddress: ''
            }
        }));
    };

    // Function to edit sublocation
    const handleEditSublocation = (sublocation) => {
        setSublocationModal(prev => ({
            ...prev,
            mode: 'edit',
            selectedSublocation: { ...sublocation }
        }));
    };

    // Function to save sublocation (add/edit)
    const handleSaveSublocation = async () => {
        if (!sublocationModal.selectedSublocation?.locationName?.trim()) {
            setApiError('Sublocation name is required');
            return;
        }

        setSublocationModal(prev => ({ ...prev, loading: true }));
        setApiError('');

        try {
            if (sublocationModal.mode === 'add') {
                // Create new sublocation
                const payload = [{
                    organizationId: organizationId,
                    parentLocationId: sublocationModal.parentLocation.id,
                    locationName: sublocationModal.selectedSublocation.locationName,
                    locationAddress: sublocationModal.selectedSublocation.locationAddress || ''
                }];
                
                await api.post('organization-location/add', payload);
            } else if (sublocationModal.mode === 'edit') {
                // Update existing sublocation
                const payload = {
                    id: sublocationModal.selectedSublocation.id,
                    organizationId: organizationId,
                    parentLocationId: sublocationModal.parentLocation.id,
                    locationName: sublocationModal.selectedSublocation.locationName,
                    locationAddress: sublocationModal.selectedSublocation.locationAddress || ''
                };
                
                await api.put('organization-location/update', payload);
            }

            // Refresh locations to get updated sublocations
            await fetchLocations();
            
            // Find the updated parent location and refresh modal data
            const updatedLocations = await api.get(
                `organization-location/get-by-organization-id?organizationId=${organizationId}`
            );
            const updatedParentLocation = updatedLocations.data.response[0]?.locations?.find(
                loc => loc.id === sublocationModal.parentLocation.id
            );

            setSublocationModal(prev => ({
                ...prev,
                mode: 'view',
                selectedSublocation: null,
                sublocations: updatedParentLocation?.subLocations || [],
                loading: false
            }));

        } catch (error) {
            console.error('Error saving sublocation:', error);
            setApiError(error?.response?.data?.message || 'Failed to save sublocation');
            setSublocationModal(prev => ({ ...prev, loading: false }));
        }
    };

    // Function to delete sublocation
    const handleDeleteSublocation = (sublocation) => {
        setSublocationDeleteModal({
            isOpen: true,
            sublocation: sublocation
        });
    };

    // Function to confirm sublocation deletion
    const confirmDeleteSublocation = async () => {
        setSublocationModal(prev => ({ ...prev, loading: true }));

        try {
            await api.delete(`organization-location/delete?id=${sublocationDeleteModal.sublocation.id}`);
            
            // Refresh locations
            await fetchLocations();
            
            // Update modal sublocations list
            const updatedLocations = await api.get(
                `organization-location/get-by-organization-id?organizationId=${organizationId}`
            );
            const updatedParentLocation = updatedLocations.data.response[0]?.locations?.find(
                loc => loc.id === sublocationModal.parentLocation.id
            );

            setSublocationModal(prev => ({
                ...prev,
                sublocations: updatedParentLocation?.subLocations || [],
                loading: false
            }));

            // Close delete modal
            setSublocationDeleteModal({
                isOpen: false,
                sublocation: null
            });

        } catch (error) {
            console.error('Error deleting sublocation:', error);
            setApiError('Failed to delete sublocation');
            setSublocationModal(prev => ({ ...prev, loading: false }));
        }
    };

    const loadMoreLocations = async () => {
        if (!hasMore) return;
        
        setLoading(true);
        try {
            const newCount = count + 10;
            const {data} = await api.get(
                `organization-location/get-by-organization-id?organizationId=${organizationId}&count=${newCount}${
                    searchTerm ? `&search=${searchTerm}` : ''
                }`
            );
            
            setLocations(data.response[0]?.locations || []);
            setCount(newCount);
            setHasMore(data.response[0]?.locations.length >= newCount);
        } catch (error) {
            console.error('Error loading more locations:', error);
        } finally {
            setLoading(false);
        }
    };

    const reloadLocations = () => {
        setCount(10);
        fetchLocations();
    };

    const handleImport = async (file) => {
        try {
            setLoading(true);
            setApiError('');
            
            // Read the Excel file
            const reader = new FileReader();
            reader.onload = async (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                // Validate organization is selected
                if (!organizationId) {
                    setApiError('Please select a organization first');
                    return;
                }

                // Transform the imported data
                const locationsToImport = jsonData
                    .map(item => ({
                        locationName: item['Name']?.toString() || '',
                        locationAddress: item['Address']?.toString() || '',
                        organizationId: organizationId
                    }))
                    .filter(location => location.locationName.trim() !== '');

                if (locationsToImport.length === 0) {
                    setApiError('No valid location data found in the file');
                    return;
                }

                try {
                    await api.post('organization-location/add', locationsToImport);
                    reloadLocations();
                    setFileModal(false);
                } catch (error) {
                    setApiError(error?.response?.data?.error || "Failed to import locations");
                }
            };
            
            reader.onerror = () => {
                setApiError("Error reading the file");
            };
            
            reader.readAsArrayBuffer(file);
        } catch (error) {
            setApiError("Error processing the file");
            console.error('Import error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFileModal(false);
        setApiError('');
    };

    const handleExport = () => {
        if (!locations || locations.length === 0) return;
        
        const dataToExport = locations.map((location, index) => ({
            '#': index + 1,
            'Name': location?.locationName || '',
            'Address': location?.locationAddress || '',
            'Sublocations Count': location?.subLocations?.length || 0
        }));
        
        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Locations');
        XLSX.writeFile(wb, `locations_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setCount(10);
        setHasMore(true);
    };

    const handleOrganizationChange = (e) => {
        setOrganizationId(e.target.value);
        setLocations([]);
        setCount(10);
        setHasMore(true);
    };

    const tog_large = (entry = null) => {
        setmodal_large(!modal_large);
        setSelectedEntry(entry || { locationName: '', locationAddress: '' });
        setErrors({});
        setApiError('');
    };

    const handleModalClose = () => {
        setmodal_large(false);
        setSelectedEntry(null);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        
        const newErrors = {};
        if (!selectedEntry?.locationName?.trim()) newErrors.locationName = 'Location name is required';
    
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
    
        try {
            setApiError('');
            
            if (selectedEntry?.id) {
                await api.put(`organization-location/update`, {
                    id: selectedEntry.id,
                    locationName: selectedEntry.locationName,
                    locationAddress: selectedEntry.locationAddress || '',
                    organizationId: organizationId
                });
            } else {
                await api.post('organization-location/add', [{
                    locationName: selectedEntry.locationName,
                    locationAddress: selectedEntry.locationAddress || '',
                    organizationId: organizationId
                }]);
            }
    
            reloadLocations();
            handleModalClose();
        } catch (error) {
            setApiError(error?.response?.data?.error || "Something went wrong!");
        }
    };

    const handleDelete = async () => {
        try {
            await api.delete(`organization-location/delete?id=${selectedEntry?.id}`);
            reloadLocations();
            setDeleteModal(false);
        } catch (error) {
            console.error('Error deleting location:', error);
            setApiError('Failed to delete location');
        }
    }

    useEffect(() => {
        fetchOrganizations();
    }, []);

    useEffect(() => {
        fetchLocations();
    }, [organizationId, count, searchTerm]);

    return (
        <div className='page-content mt-4'>
            <div className="mb-4 d-flex align-items-center gap-3">
                <motion.div
                    className="mb-4 d-flex align-items-center gap-3"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <PermissionGuard permission="location:create">
                        <button
                            type="button"
                            className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
                            onClick={() => tog_large()}
                        >
                            <FeatherIcon icon="plus" />
                            Add Location
                        </button>
                    </PermissionGuard>
                    <div className="ms-auto" style={{ minWidth: '400px', flexGrow: 1, maxWidth: '500px' }}>
                        <Input
                            type="text"
                            placeholder="Search locations..."
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                </motion.div>
            </div>
            
            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="card-title text-success mb-0">Locations</h3>
                        <div className="d-flex align-items-center gap-2">
                            <button 
                                className="btn btn-soft-info p-2"
                                onClick={() => setFileModal(true)}
                                title="Import/Export locations"
                            >
                                <FeatherIcon icon="file-text" size={18} />
                            </button>
                            <select 
                                className="form-select form-select-md w-auto" 
                                value={organizationId || ''}
                                onChange={handleOrganizationChange}
                            >
                                <option value="" disabled>Select Organization</option>
                                {organizations?.map((organization) => (
                                    <option key={organization.id} value={organization.id}>
                                        {organization?.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </CardHeader>
                
                <CardBody>
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Address</th>
                                    <th>Sublocations</th>
                                    <th>Edit</th>
                                    <th>Delete</th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                    {locations.map((location, ind) => (
                                        <motion.tr
                                            key={location?.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <th scope="row">{ind+1}</th>
                                            <td>{location?.locationName}</td>
                                            <td>{location?.locationAddress}</td>
                                            <td>
                                                {location?.subLocations?.length > 0 ? (
                                                    <button 
                                                        className="btn btn-sm btn-outline-info d-flex align-items-center gap-1"
                                                        onClick={() => handleSublocationsClick(location)}
                                                        title="View Sublocations"
                                                    >
                                                        <FeatherIcon icon="list" size={14} />
                                                        {location.subLocations.length}
                                                    </button>
                                                ) : (
                                                    <button 
                                                        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                                        onClick={() => handleSublocationsClick(location)}
                                                        title="Add Sublocations"
                                                    >
                                                        <FeatherIcon icon="plus" size={14} />
                                                        Add
                                                    </button>
                                                )}
                                            </td>
                                            <td>
                                                <PermissionGuard permission="location:update">
                                                    <span className='text-warning' role='button' onClick={() => tog_large(location)}>
                                                        <FeatherIcon icon="edit" />
                                                    </span>
                                                </PermissionGuard>
                                            </td>
                                            <td>
                                                <PermissionGuard permission="location:delete">
                                                    <span className='text-danger' role='button' onClick={() => {
                                                        setSelectedEntry(location);
                                                        setDeleteModal(true);
                                                    }}>
                                                        <FeatherIcon icon="trash-2" />
                                                    </span>
                                                </PermissionGuard>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>

                    {loading && (
                        <div className="text-center py-3">
                            <Spinner color="primary" />
                        </div>
                    )}

                    <div className="text-center mt-3">
                        {hasMore ? (
                            <Button
                                color="primary"
                                onClick={loadMoreLocations}
                                disabled={loading}
                            >
                                {loading ? 'Loading...' : 'Load More'}
                            </Button>
                        ) : (
                            locations.length > 0 && (
                                <div className="text-muted">No more locations to load</div>
                            )
                        )}
                    </div>

                    {locations.length === 0 && !loading && (
                        <div className="text-center py-5 text-muted">
                            {searchTerm
                                ? "No locations match your search"
                                : "No locations found"}
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Sublocation Modal */}
            <Modal 
                size="lg" 
                isOpen={sublocationModal.isOpen} 
                toggle={() => setSublocationModal(prev => ({ ...prev, isOpen: false }))}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-header">
                        <h5 className="modal-title">
                            Sublocations - {sublocationModal.parentLocation?.locationName}
                        </h5>
                        <button 
                            onClick={() => setSublocationModal(prev => ({ ...prev, isOpen: false }))} 
                            type="button" 
                            className="btn-close" 
                            aria-label="Close"
                        ></button>
                    </div>
                    
                    <div className="modal-body">
                        {sublocationModal.mode === 'view' && (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">Sublocations List</h6>
                                    <button 
                                        className="btn btn-sm btn-success"
                                        onClick={handleAddSublocation}
                                    >
                                        <FeatherIcon icon="plus" size={14} className="me-1" />
                                        Add Sublocation
                                    </button>
                                </div>
                                
                                {sublocationModal.loading ? (
                                    <div className="text-center py-3">
                                        <Spinner color="primary" />
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        {sublocationModal.sublocations.length > 0 ? (
                                            <table className="table table-hover table-sm">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Name</th>
                                                        <th>Address</th>
                                                        <th>Created By</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sublocationModal.sublocations.map((sublocation, index) => (
                                                        <tr key={sublocation.id}>
                                                            <td>{index + 1}</td>
                                                            <td>{sublocation.locationName}</td>
                                                            <td>{sublocation.locationAddress || '-'}</td>
                                                            <td>{sublocation.createdBy?.name || '-'}</td>
                                                            <td>
                                                                <button 
                                                                    className="btn btn-sm btn-outline-warning me-1"
                                                                    onClick={() => handleEditSublocation(sublocation)}
                                                                    title="Edit"
                                                                >
                                                                    <FeatherIcon icon="edit" size={12} />
                                                                </button>
                                                                <button 
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => handleDeleteSublocation(sublocation)}
                                                                    title="Delete"
                                                                >
                                                                    <FeatherIcon icon="trash-2" size={12} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <div className="text-center py-4 text-muted">
                                                No sublocations found. Click "Add Sublocation" to create one.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        {(sublocationModal.mode === 'add' || sublocationModal.mode === 'edit') && (
                            <form onSubmit={(e) => { e.preventDefault(); handleSaveSublocation(); }}>
                                <h6 className="mb-3">
                                    {sublocationModal.mode === 'add' ? 'Add New Sublocation' : 'Edit Sublocation'}
                                </h6>
                                
                                <Row className="mb-3">
                                    <label className="col-sm-3 col-form-label">Name *</label>
                                    <Col sm={9}>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={sublocationModal.selectedSublocation?.locationName || ''}
                                            onChange={(e) => setSublocationModal(prev => ({
                                                ...prev,
                                                selectedSublocation: {
                                                    ...prev.selectedSublocation,
                                                    locationName: e.target.value
                                                }
                                            }))}
                                            placeholder="Enter sublocation name"
                                            required
                                        />
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <label className="col-sm-3 col-form-label">Address</label>
                                    <Col sm={9}>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={sublocationModal.selectedSublocation?.locationAddress || ''}
                                            onChange={(e) => setSublocationModal(prev => ({
                                                ...prev,
                                                selectedSublocation: {
                                                    ...prev.selectedSublocation,
                                                    locationAddress: e.target.value
                                                }
                                            }))}
                                            placeholder="Enter sublocation address"
                                        />
                                    </Col>
                                </Row>

                                {apiError && (
                                    <Alert color="danger" className="mb-3">
                                        {apiError}
                                    </Alert>
                                )}

                                <div className="d-flex gap-2">
                                    <button
                                        type="submit"
                                        className="btn btn-success"
                                        disabled={sublocationModal.loading}
                                    >
                                        {sublocationModal.loading ? (
                                            <>
                                                <Spinner size="sm" className="me-1" />
                                                Saving...
                                            </>
                                        ) : (
                                            sublocationModal.mode === 'add' ? 'Add Sublocation' : 'Update Sublocation'
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setSublocationModal(prev => ({
                                            ...prev,
                                            mode: 'view',
                                            selectedSublocation: null
                                        }))}
                                        disabled={sublocationModal.loading}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </motion.div>
            </Modal>

            {/* Sublocation Delete Modal */}
            <Modal isOpen={sublocationDeleteModal.isOpen} toggle={() => setSublocationDeleteModal({ isOpen: false, sublocation: null })}>
                <div className="modal-header">
                    <h5 className="modal-title">Confirm Delete Sublocation</h5>
                    <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setSublocationDeleteModal({ isOpen: false, sublocation: null })} 
                        aria-label="Close"
                    ></button>
                </div>
                <div className="modal-body">
                    <p>Are you sure you want to delete the sublocation <strong>"{sublocationDeleteModal.sublocation?.locationName}"</strong>? This action cannot be undone.</p>
                </div>
                <div className="modal-footer">
                    <button 
                        type="button" 
                        className="btn btn-secondary" 
                        onClick={() => setSublocationDeleteModal({ isOpen: false, sublocation: null })}
                    >
                        Cancel
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-danger" 
                        onClick={confirmDeleteSublocation}
                        disabled={sublocationModal.loading}
                    >
                        {sublocationModal.loading ? (
                            <>
                                <Spinner size="sm" className="me-1" />
                                Deleting...
                            </>
                        ) : (
                            'Delete'
                        )}
                    </button>
                </div>
            </Modal>

            {/* Add/Edit Modal */}
            <Modal size="lg" isOpen={modal_large} toggle={() => tog_large()}>
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-header">
                        <h5 className="modal-title" id="myLargeModalLabel">
                            {selectedEntry?.id ? 'Edit Location' : 'Add Location'}
                        </h5>
                        <button onClick={handleModalClose} type="button" className="btn-close" aria-label="Close"></button>
                    </div>
                    
                    <div className="modal-body">
                        <form onSubmit={handleUpdate}>
                            <Row className="mb-4">
                                <label htmlFor="locationName" className="col-sm-3 col-form-label">Name</label>
                                <Col sm={9}>
                                    <input
                                        type="text"
                                        id="locationName"
                                        className={`form-control ${errors.locationName ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.locationName || ''}
                                        onChange={(e) => setSelectedEntry({...selectedEntry, locationName: e.target.value})}
                                    />
                                    {errors.locationName && <small className="text-danger">{errors.locationName}</small>}
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <label htmlFor="locationAddress" className="col-sm-3 col-form-label">Address</label>
                                <Col sm={9}>
                                    <input
                                        type="text"
                                        id="locationAddress"
                                        className={`form-control ${errors.locationAddress ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.locationAddress || ''}
                                        onChange={(e) => setSelectedEntry({...selectedEntry, locationAddress: e.target.value})}
                                    />
                                    {errors.locationAddress && <small className="text-danger">{errors.locationAddress}</small>}
                                </Col>
                            </Row>

                            <div className="mt-4">
                                {apiError && <div className="alert alert-danger">{apiError}</div>}
                                <button type="submit" className="btn btn-success w-lg transition-all hover-grow pulse-on-hover">
                                    {selectedEntry?.id ? 'Update' : 'Add'}
                                </button>
                            </div>
                        </form>
                    </div>
                </motion.div>
            </Modal>
            
            {/* Delete Modal */}
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
                <div className="modal-header">
                    <h5 className="modal-title">Confirm Delete</h5>
                    <button type="button" className="btn-close" onClick={() => setDeleteModal(false)} aria-label="Close"></button>
                </div>
                <div className="modal-body">
                    <p>Are you sure you want to delete this Location? This action cannot be undone.</p>
                </div>
                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setDeleteModal(false)}>
                        Cancel
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-danger" 
                        onClick={handleDelete}
                    >
                        Delete
                    </button>
                </div>
            </Modal>

            <FileModal 
                title='Locations'
                isOpen={fileModal} 
                toggle={handleClose}
                data={locations}
                onImport={handleImport}
                onExport={handleExport}
                error={apiError}
                loading={loading}
                importLabel="Import Locations from Excel"
                exportLabel="Export Locations to Excel"
            />
        </div>
    );
};

export default Location;